<script setup lang="ts">
import { ASSETS, LX_BIDS, NAV_LINKS } from './constants'
import lx from '../../utils/lx'
import { scrollToSection } from '../../utils/scrollToSection'

defineProps<{
  contactLabel: string
  copyStatus: 'idle' | 'success' | 'error'
}>()

const emit = defineEmits<{
  contact: []
}>()

function onNavClick(event: MouseEvent, buttonName: string, href: string): void {
  event.preventDefault()
  lx.moduleClick(LX_BIDS.navMc, { button_name: buttonName })
  scrollToSection(href)
}

function onBrandClick(event: MouseEvent): void {
  event.preventDefault()
  scrollToSection('#top')
}
</script>

<template>
  <header class="nav-shell" :data-mv-bid="LX_BIDS.navMv" data-mv-key="nav">
    <nav class="navbar">
      <a class="brand" href="#top" @click="onBrandClick">
        <img :src="ASSETS.logo" alt="美团智播" width="119" height="29">
      </a>
      <div class="nav-links">
        <a
          v-for="link in NAV_LINKS"
          :key="link.href"
          :href="link.href"
          @click="onNavClick($event, link.label, link.href)"
        >{{ link.label }}</a>
      </div>
      <mtd-button class="contact-button" html-type="button" @click="emit('contact')">
        {{ contactLabel }}
        <span v-if="copyStatus === 'success'" class="contact-status">已复制</span>
      </mtd-button>
    </nav>
  </header>
</template>

<style scoped lang="scss">
.nav-shell {
  position: fixed;
  z-index: 30;
  top: 16px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  pointer-events: none;
}

.navbar {
  position: relative;
  pointer-events: auto;
  display: flex;
  align-items: center;
  width: 1200px;
  max-width: calc(100% - 48px);
  height: 71px;
  padding: 0 30px;
  border: 0;
  border-radius: 40px;
  /* 印迹底：半透明白；描边用 ::before 只画一圈，避免整块被紫渐变染透 */
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.52) 0%,
    rgba(255, 255, 255, 0.36) 100%
  );
  backdrop-filter: blur(24px) saturate(1.45);
  -webkit-backdrop-filter: blur(24px) saturate(1.45);
  box-shadow:
    0 -7px 58px -10px #9012ff1e,
    inset 0 1px 0 rgba(255, 255, 255, 0.7),
    inset 0 -0.5px 0 rgba(255, 255, 255, 0.25);
}

.navbar::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 0;
  border-radius: inherit;
  padding: 2px;
  background: linear-gradient(92deg, #953eff17 1%, #953eff21 99%);
  pointer-events: none;
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
}

.navbar > * {
  position: relative;
  z-index: 1;
}

.brand {
  display: flex;
  flex: none;
  margin-right: 29px;
}

.brand img {
  width: 119px;
  height: 29px;
  object-fit: contain;
}

.nav-links {
  display: flex;
  flex: 1;
  align-items: center;
  gap: 43px;
}

.nav-links a {
  color: #171717;
  opacity: 0.9;
  font-size: 16px;
  font-weight: 500;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;
}

.nav-links a:hover {
  color: #8932ff;
  opacity: 1;
}

.contact-button {
  position: relative;
  flex: none;
  min-width: 80px;
  height: 32px;
  padding: 8px 12px !important;
  border: 0 !important;
  border-radius: 291px !important;
  background: #171717 !important;
  color: #fff !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  line-height: 1 !important;
}

.contact-status {
  position: absolute;
  top: 40px;
  right: 0;
  padding: 4px 8px;
  border-radius: 6px;
  background: #171717;
  color: #fff;
  font-size: 12px;
  white-space: nowrap;
}

@media (max-width: 960px) {
  .nav-shell {
    top: 0;
  }

  .navbar {
    width: 100%;
    max-width: none;
    height: 56px;
    padding: 0 16px;
    border-radius: 0;
    border-bottom: 1px solid rgba(149, 62, 255, 0.12);
  }

  .navbar::before {
    display: none;
  }

  .nav-links {
    display: none;
  }

  .brand img {
    width: auto;
    height: 24px;
  }
}
</style>
